// hooks/useTemplates.js
import { useState, useEffect } from 'react';
import { 
  fetchMaler, 
  updateMal, 
  deleteMal 
} from '../services/malService';

/**
 * Custom hook for managing templates
 * @param {string} apiBaseUrl - The base URL for the API
 * @param {string} userId - The current user ID
 * @param {string} defaultTemplate - The default template name to use
 */
const useTemplates = (apiBaseUrl, userId, defaultTemplate) => {
  const [templates, setTemplates] = useState({});
  const [chosenTemplate, setChosenTemplate] = useState(defaultTemplate);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isTemplateSaving, setIsTemplateSaving] = useState(false);

  // Fetch templates when userId changes or becomes available
  useEffect(() => {
    if (!userId) {
      console.log("No userId available yet, waiting...");
      return;
    }
  
    console.log("Fetching templates with userId:", userId);
    
    fetchMaler(apiBaseUrl, userId)
      .then(res => {
        console.log("Templates fetched successfully:", res);
        setTemplates(res);
      })
      .catch(err => {
        console.error("Error fetching templates:", {
          message: err.message,
          response: err.response?.data,
          config: err.config?.headers
        });
      });
  }, [apiBaseUrl, userId]);

  /**
   * Open the template dialog for creating a new template
   */
  const openNewTemplateDialog = () => {
    setEditingTemplate(null);
    setIsTemplateDialogOpen(true);
  };

  /**
   * Open the template dialog for editing an existing template
   * @param {string} title - The title of the template to edit
   * @param {string} content - The content of the template to edit
   */
  const openEditTemplateDialog = (title, content) => {
    setEditingTemplate({ title, content });
    setIsTemplateDialogOpen(true);
  };

  /**
   * Handle saving a template (create or update)
   * @param {string} title - The title of the template
   * @param {string} content - The content of the template
   * @param {string|null} oldTitle - The original title if updating
   */
  const handleSaveTemplate = async (title, content, oldTitle = null) => {
    setIsTemplateSaving(true);
    
    try {
      const updatedTemplates = await updateMal(apiBaseUrl, userId, title, content, oldTitle);
      setTemplates(updatedTemplates);
      setEditingTemplate(null);
      setIsTemplateDialogOpen(false);
    } catch (error) {
      console.error("Error saving template:", error);
      alert("Kunne ikke lagre malen. Prøv igjen senere.");
    } finally {
      setIsTemplateSaving(false);
    }
  };

  /**
   * Handle updating a template from the dialog form
   * @param {string} title - The new title for the template
   * @param {string} content - The content for the template
   * @param {string|null} oldTitle - The original title if updating an existing template
   */
  const handleUpdateTemplate = async (title, content, oldTitle = null) => {
    try {
      if (oldTitle) {
        // When updating an existing template
        await updateMal(apiBaseUrl, userId, title, content, oldTitle);
      } else {
        // When creating a new template
        await handleSaveTemplate(title, content);
      }
    } catch (error) {
      console.error("Error updating template:", error);
      throw error;
    }
  };

  /**
   * Handle deleting a template
   * @param {string} title - The title of the template to delete
   * @param {boolean} skipConfirmation - Whether to skip the confirmation dialog
   */
  const handleDeleteTemplate = async (title, skipConfirmation = false) => {
    // Skip confirmation if requested
    if (!skipConfirmation && !window.confirm(`Er du sikker på at du vil slette malen "${title}"?`)) {
      return;
    }
    
    try {
      const updatedTemplates = await deleteMal(apiBaseUrl, userId, title);
      setTemplates(updatedTemplates);
      
      // If the deleted template was the chosen one, reset to default
      if (chosenTemplate === title) {
        setChosenTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("Kunne ikke slette malen. Prøv igjen senere.");
    }
  };

  /**
   * Filter templates based on search query
   * @returns {Array} - Array of [title, content] pairs matching the search query
   */
  const filteredTemplates = Object.entries(templates).filter(([title, content]) => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    const contentStr = typeof content === 'string' ? content : '';
    return (
      title.toLowerCase().includes(searchLower) ||
      contentStr.toLowerCase().includes(searchLower)
    );
  });

  return {
    templates,
    chosenTemplate,
    setChosenTemplate,
    editingTemplate,
    setEditingTemplate,
    searchQuery,
    setSearchQuery,
    isTemplateDialogOpen,
    setIsTemplateDialogOpen,
    isTemplateSaving,
    openNewTemplateDialog,
    openEditTemplateDialog,
    handleSaveTemplate,
    handleDeleteTemplate,
    handleUpdateTemplate,
    filteredTemplates
  };
};

export default useTemplates;